<script setup lang="ts">
/**
 * Todos: the team's shared list (drag to prioritize: first = most important, or
 * let AI order it with a reason per todo) and your own list. Assign a person or
 * an agent; agents pick their todos up by themselves or on "Start", and todos
 * flagged for the night shift are worked off in the night window. Problems
 * from Flare, CI, GitHub issues and Schorschi arrive here by intake.
 */
import {
  Bot, Check, ChevronDown, ChevronUp, CircleDot, Clock, ExternalLink, FileText, Flame, GitPullRequest, GripVertical, Link2, ListTodo, Loader, MoonStar, Play, Plus, RotateCcw, Sparkles, Trash2, User, Users,
} from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, reactive, ref, type Component } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import { createTodo, deleteTodo, reorderTodos, setAutoAgent, startTodo, updateTodo, useAgents, useTodos, type Todo } from "@/api/agents";
import { NIGHT_SHIFT_KEY, prioritizeTodos, runNightPhase, useNightShift, type PrioritizeResult } from "@/components/agents/nightShift";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import MarkdownView from "@/components/common/MarkdownView.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs, { type PageTabItem } from "@/components/common/PageTabs.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import TagInput from "@/components/common/TagInput.vue";
import NightShiftPanel from "@/components/todos/NightShiftPanel.vue";
import PrioritizeSummary from "@/components/todos/PrioritizeSummary.vue";
import SourceBadge from "@/components/todos/SourceBadge.vue";
import TodoRunSummary from "@/components/todos/TodoRunSummary.vue";
import { ESTIMATE_SHORT, ESTIMATES, isNight, linkLabel, nightVerdict, plainTags, pullRequests, repoOf, sortedLinks, withNight, type TodoItem } from "@/components/todos/todoMeta";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { confirm } from "@/composables/useConfirm";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { errorMessage } from "@/lib/errors";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Todos" } });

const route = useRoute();
const queryClient = useQueryClient();
const { can } = useSession();
const tab = useRouteQuery("list", "shared");
const list = computed<"shared" | "personal">(() => (tab.value === "mine" || !can("todos.read") ? "personal" : "shared"));
const showDone = ref(false);
const todos = useTodos(list, showDone);
// Open counts for the tabs (the same queries as the lists while "Done" is off).
// Without todos.read there is no shared tab: the query falls back to your own list (cached, no extra request).
const sharedOpen = useTodos(() => (can("todos.read") ? "shared" : "personal"), false);
const mineOpen = useTodos("personal", false);
const openCount = (q: typeof sharedOpen) => q.data.value?.items.filter((t) => t.status !== "done" && t.status !== "cancelled").length;
const listTabs = computed<PageTabItem[]>(() => [
  { id: "shared", label: "Shared", icon: Users, badge: openCount(sharedOpen) },
  { id: "mine", label: "Mine", icon: User, badge: openCount(mineOpen) },
]);
const listTab = computed({ get: () => (list.value === "personal" ? "mine" : "shared"), set: (v: string) => (tab.value = v) });
const agents = useAgents();
const shared = computed(() => list.value === "shared");
const nightShift = useNightShift(() => shared.value && can("agents.read"));
const highlight = computed(() => String(route.query.highlight ?? ""));
const expanded = ref<string | null>(highlight.value || null);

const refresh = () => queryClient.invalidateQueries({ queryKey: ["todos"] });
const agentOptions = computed(() => (agents.data.value?.items ?? []).filter((a) => a.enabled).map((a) => ({ value: a.name, label: `${a.title}${a.autoTodos ? "" : " (manual start)"}` })));
const agentTitle = (name: string | null | undefined) => (name ? (agents.data.value?.items.find((a) => a.name === name)?.title ?? name) : null);
const priorityOptions = ["urgent", "high", "normal", "low"];
const PRIORITY_VARIANT: Record<string, "danger" | "warning" | "outline" | "muted"> = { urgent: "danger", high: "warning", normal: "outline", low: "muted" };
/** The left edge of a todo card: urgent and high stand out in the list. */
const PRIORITY_ACCENT: Record<string, string | undefined> = { urgent: "bg-destructive", high: "bg-warning" };
const LINK_ICON: Record<string, Component> = { pr: GitPullRequest, issue: CircleDot, error: Flame, run: Bot, doc: FileText, url: Link2 };
/** Changing the shared list needs todos.write; your own list is yours. */
const mayWrite = computed(() => !shared.value || can("todos.write"));

// --- add
const draft = reactive({ title: "", priority: "normal" as Todo["priority"], agent: undefined as string | undefined });
async function add() {
  if (!draft.title.trim()) return;
  try {
    await createTodo({ list: list.value, title: draft.title.trim(), priority: draft.priority, ...(draft.agent ? { agent: draft.agent } : {}) });
    draft.title = "";
    draft.agent = undefined;
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

/** Change a todo (what the patch leaves out stays as it is). */
async function patch(t: Todo, body: Parameters<typeof updateTodo>[1]) {
  try {
    await updateTodo(t.id, body);
    await refresh();
    return true;
  } catch (err) {
    toast.error(errorMessage(err));
    return false;
  }
}

async function start(t: Todo) {
  try {
    const u = await startTodo(t.id);
    toast.success(`Started; follow the run.`, { action: u.runId ? { label: "Open", onClick: () => window.location.assign(`/agents/runs/${u.runId}`) } : undefined });
    await refresh();
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

async function remove(t: Todo) {
  if (!(await confirm({ title: `Delete “${t.title}”?`, destructive: true, confirmLabel: "Delete" }))) return;
  await deleteTodo(t.id);
  await refresh();
}

// --- the night shift flag
async function toggleNight(t: TodoItem) {
  const on = !isNight(t);
  if (!(await patch(t, { tags: withNight(t.tags, on) }))) return;
  if (!on) return void toast.success("Taken off the night shift.");
  // Whether dispatch will take it, and with which agent (the same checks as src/agents/nightshift.ts dispatch).
  const s = nightShift.data.value;
  const verdict = nightVerdict(t, s ? { config: s.config, open: s.window.open, agents: agents.data.value?.items ?? [] } : null);
  toast[verdict.ok ? "success" : "info"](verdict.text);
}

// --- ordering (drag and drop, or the arrows)
const items = computed(() => (todos.data.value?.items ?? []) as TodoItem[]);
const open = computed(() => items.value.filter((t) => t.status !== "done" && t.status !== "cancelled"));
const closed = computed(() => items.value.filter((t) => t.status === "done" || t.status === "cancelled"));
const dragging = ref<string | null>(null);
async function move(ids: string[]) {
  queryClient.setQueryData(["todos", list.value, showDone.value], (old: { items: Todo[] } | undefined) =>
    old ? { ...old, items: [...ids.map((id) => old.items.find((t) => t.id === id)!), ...old.items.filter((t) => !ids.includes(t.id))] } : old,
  );
  try {
    await reorderTodos(list.value, ids);
  } catch (err) {
    toast.error(errorMessage(err));
  }
  await refresh();
}
function onDrop(target: Todo) {
  const from = dragging.value;
  dragging.value = null;
  if (!from || from === target.id) return;
  const ids = open.value.map((t) => t.id).filter((id) => id !== from);
  ids.splice(ids.indexOf(target.id), 0, from);
  void move(ids);
}
function step(t: Todo, delta: number) {
  const ids = open.value.map((x) => x.id);
  const i = ids.indexOf(t.id);
  const j = i + delta;
  if (j < 0 || j >= ids.length) return;
  [ids[i], ids[j]] = [ids[j], ids[i]];
  void move(ids);
}

// --- prioritize with AI (shared list; billed to you)
const mayPrioritize = computed(() => shared.value && can("todos.write") && can("ai.exec"));
const prioritizing = ref(false);
const prioritized = ref<PrioritizeResult | null>(null);
const orderBefore = ref<string[] | null>(null);
const undoing = ref(false);

/**
 * Prioritize with AI after a confirmation, keeping the order from before for Undo. From the night shift panel
 * (Run now) it runs as the night shift's phase: while the window is open it counts as tonight's prioritization.
 */
async function prioritize(via: "page" | "night-shift" = "page") {
  const tonight = via === "night-shift" && nightShift.data.value?.window.open;
  const ok = await confirm({
    title: "Prioritize the shared list with AI?",
    description: `Orders the open todos by impact and sets each one's priority, with a short reason, an effort estimate and a suggested agent. The list is reordered for everyone; billed to your AI budget${tonight ? " and counted against tonight's night shift limit" : ""}. You can undo the order and priorities afterwards.`,
    confirmLabel: "Prioritize",
  });
  if (!ok) return;
  const before = open.value.map((t) => t.id);
  prioritizing.value = true;
  try {
    if (via === "night-shift") {
      const res = await runNightPhase("prioritize");
      if (res.prioritization) showPrioritized(res.prioritization, before);
      void queryClient.invalidateQueries({ queryKey: NIGHT_SHIFT_KEY });
    } else showPrioritized(await prioritizeTodos(false), before);
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    prioritizing.value = false;
  }
}

function showPrioritized(result: PrioritizeResult, before: string[]) {
  prioritized.value = result;
  orderBefore.value = before;
  void refresh();
}

/** Put back the order and priorities from before (the reasons stay). */
async function undoPrioritize() {
  const result = prioritized.value;
  const before = orderBefore.value;
  if (!result || !before) return;
  undoing.value = true;
  try {
    const now = open.value.map((t) => t.id);
    const ids = [...before.filter((id) => now.includes(id)), ...now.filter((id) => !before.includes(id))];
    if (ids.length) await reorderTodos("shared", ids);
    for (const c of result.items.filter((i) => i.priority !== i.previousPriority && now.includes(i.id))) {
      const t = open.value.find((x) => x.id === c.id);
      if (t) await updateTodo(t.id, { priority: c.previousPriority as Todo["priority"] });
    }
    toast.success("Order and priorities restored.");
    prioritized.value = null;
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    undoing.value = false;
    await refresh();
  }
}

// --- auto agent of the list
const autoAgent = computed(() => {
  const id = todos.data.value?.autoAgentId;
  return id ? agents.data.value?.items.find((a) => a.id === id)?.name : undefined;
});
async function changeAuto(name: string | undefined) {
  try {
    await setAutoAgent(list.value, name ?? null);
    toast.success(name ? `New unassigned todos go to ${name}.` : "No auto agent.");
    await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: ["agents"] })]);
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
const mayAuto = computed(() => list.value === "personal" || can("agents.admin"));
const editing = reactive<Record<string, { body: string }>>({});
</script>

<template>
  <div class="flex flex-col gap-6">
    <PageHeader
      title="Todos"
      description="The team's shared list — first is most important — and your own. Assign a person or an agent; agents pick up their todos and write back what they found. Todos flagged for the night shift are worked off at night."
    >
      <template #actions>
        <Button v-if="mayPrioritize" variant="outline" :disabled="prioritizing || !open.length" @click="prioritize()">
          <Loader v-if="prioritizing" class="animate-spin" /><Sparkles v-else /> {{ prioritizing ? "Prioritizing…" : "Prioritize with AI" }}
        </Button>
        <label class="flex items-center gap-2 text-sm"><Switch v-model="showDone" /> Done</label>
      </template>
      <template v-if="can('todos.read')" #tabs>
        <PageTabs v-model="listTab" :items="listTabs" label="Lists" id-prefix="todo-list" />
      </template>
    </PageHeader>

    <NightShiftPanel v-if="shared && can('agents.read')" :prioritizing="prioritizing" @prioritize="prioritize('night-shift')" />

    <div v-if="mayWrite || mayAuto" class="flex flex-col gap-3">
      <form v-if="mayWrite" class="flex flex-wrap gap-2" @submit.prevent="add">
        <Input v-model="draft.title" :placeholder="shared ? 'Add to the shared list…' : 'Add to your list…'" class="min-w-60 flex-1" aria-label="New todo" />
        <SimpleSelect v-model="draft.priority" :options="priorityOptions" class="w-28" aria-label="Priority" />
        <SimpleSelect v-model="draft.agent" :options="agentOptions" allow-empty empty-label="No agent" class="w-52" aria-label="Agent" />
        <Button type="submit" :disabled="!draft.title.trim()"><Plus /> Add</Button>
      </form>

      <div v-if="mayAuto" class="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Bot class="size-4" aria-hidden="true" /> New unassigned todos on this list go to
        <SimpleSelect :model-value="autoAgent" :options="agentOptions" allow-empty empty-label="nobody (assign by hand)" class="w-64" aria-label="Auto agent" @update:model-value="changeAuto" />
      </div>
    </div>

    <PrioritizeSummary
      v-if="prioritized && shared"
      :result="prioritized"
      :can-undo="Boolean(orderBefore)"
      :undoing="undoing"
      @undo="undoPrioritize"
      @close="prioritized = null"
    />

    <ListSkeleton v-if="todos.isPending.value" :rows="6" />
    <ErrorState v-else-if="todos.error.value" :error="todos.error.value" @retry="todos.refetch()" />
    <template v-else-if="todos.data.value">
      <EmptyState
        v-if="!open.length && !closed.length"
        :icon="ListTodo"
        title="Nothing to do"
        :description="mayWrite ? (shared ? 'Add the first one above — or let the night shift bring in new errors, failed CI runs and issues.' : 'Add the first one above.') : undefined"
        compact
      />
      <EmptyState v-else-if="!open.length" :icon="Check" title="All done" description="Every todo on this list is done." compact />
      <ol class="flex flex-col gap-2">
        <li
          v-for="(t, i) in open"
          :key="t.id"
          :draggable="mayWrite"
          :class="
            cn(
              'group/todo relative overflow-hidden rounded-xl border bg-card py-2.5 pr-2 pl-3 shadow-xs transition-[border-color,box-shadow,opacity] duration-150 hover:border-border-strong',
              dragging === t.id && 'opacity-50',
              highlight === t.id && 'ring-2 ring-primary',
              isNight(t) && shared && 'border-night/40 hover:border-night/60',
              expanded === t.id && 'shadow-sm',
            )
          "
          @dragstart="dragging = t.id"
          @dragend="dragging = null"
          @dragover.prevent
          @drop.prevent="onDrop(t)"
        >
          <span v-if="PRIORITY_ACCENT[t.priority]" :class="cn('absolute inset-y-0 left-0 w-[3px]', PRIORITY_ACCENT[t.priority])" aria-hidden="true" />
          <div class="flex items-start gap-2.5">
            <GripVertical v-if="mayWrite" class="mt-0.5 -mr-1 hidden size-4 shrink-0 cursor-grab text-muted-foreground/60 transition-colors group-hover/todo:text-muted-foreground sm:block" aria-hidden="true" />
            <span class="mt-0.5 hidden w-5 shrink-0 text-right text-xs leading-5 tabular-nums text-muted-foreground sm:block">{{ i + 1 }}</span>
            <button
              type="button"
              class="group/check mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border-[1.5px] border-border-strong transition-colors hover:border-success hover:bg-success-soft disabled:pointer-events-none"
              :aria-label="`Mark ${t.title} done`"
              :disabled="!mayWrite"
              @click="patch(t, { status: 'done' })"
            >
              <Check class="size-3 text-success opacity-0 transition-opacity group-hover/check:opacity-100" aria-hidden="true" />
            </button>
            <div class="min-w-0 flex-1">
              <button type="button" class="w-full rounded-sm text-left leading-6 font-medium [overflow-wrap:anywhere]" :aria-expanded="expanded === t.id" @click="expanded = expanded === t.id ? null : t.id">{{ t.title }}</button>
              <p v-if="t.priorityReason" class="mt-0.5 flex items-start gap-1 text-xs text-muted-foreground" :title="t.prioritizedAt ? `Prioritized ${formatDate(t.prioritizedAt)}` : undefined">
                <Sparkles class="mt-0.5 size-3 shrink-0 text-ai" aria-label="Why here" />
                <span class="line-clamp-2">{{ t.priorityReason }}</span>
              </p>
              <div class="mt-1 flex flex-wrap items-center gap-1 empty:hidden">
                <Badge v-if="t.priority !== 'normal'" :variant="PRIORITY_VARIANT[t.priority]">{{ t.priority }}</Badge>
                <Badge v-if="t.estimate" variant="outline" class="font-normal" :title="`Estimate ${t.estimate}: ${ESTIMATES[t.estimate]}`"><Clock /> {{ ESTIMATE_SHORT[t.estimate] }}</Badge>
                <Badge v-if="t.status !== 'open'" :variant="t.status === 'blocked' ? 'warning' : 'info'" class="font-normal">{{ t.status.replace("_", " ") }}</Badge>
                <Badge v-if="isNight(t) && shared && !mayWrite" variant="info" class="font-normal" title="Flagged for the night shift"><MoonStar /> night shift</Badge>
                <SourceBadge :todo="t" />
                <Badge v-if="repoOf(t)" variant="muted" class="font-mono font-normal">{{ repoOf(t) }}</Badge>
                <Badge v-if="t.assigneeName" variant="outline" class="font-normal"><User /> {{ t.assigneeName }}</Badge>
                <Badge v-if="t.assigneeAgentName" variant="outline" class="font-normal"><Bot /> {{ t.assigneeAgentName }}</Badge>
                <Badge v-else-if="t.suggestedAgentName" variant="outline" class="border-dashed font-normal text-muted-foreground" title="Suggested by the prioritization"><Bot /> {{ agentTitle(t.suggestedAgentName) }}?</Badge>
                <a v-for="l in pullRequests(t)" :key="l.url" :href="l.url" target="_blank" rel="noopener" class="rounded-full" :title="l.title ?? l.url">
                  <Badge variant="success" class="font-normal"><GitPullRequest /> {{ linkLabel(l) }}</Badge>
                </a>
                <RouterLink v-if="t.runId" :to="`/agents/runs/${t.runId}`" class="rounded-full"><Badge variant="outline" class="font-normal hover:bg-muted"><Bot /> run</Badge></RouterLink>
                <span v-if="t.dueAt" class="text-xs text-muted-foreground">due {{ formatDate(t.dueAt) }}</span>
              </div>
            </div>
            <div class="flex shrink-0 items-center">
              <Button v-if="t.assigneeAgentId && t.status === 'open' && !t.runId && can('ai.exec')" size="icon-sm" variant="ghost" :aria-label="`Start the agent on ${t.title}`" title="Start the agent now" @click="start(t)"><Play /></Button>
              <Button
                v-if="shared && mayWrite"
                size="icon-sm"
                variant="ghost"
                :aria-pressed="isNight(t)"
                :aria-label="isNight(t) ? `Take ${t.title} off the night shift` : `Flag ${t.title} for the night shift`"
                :title="isNight(t) ? 'Flagged for the night shift (click to take it off)' : 'For the night shift: an agent works on it tonight'"
                :class="isNight(t) ? 'bg-night/15 text-night hover:bg-night/25' : 'text-muted-foreground'"
                @click="toggleNight(t)"
              >
                <MoonStar />
              </Button>
              <template v-if="mayWrite">
                <!-- Phones move a todo from its details (the row keeps room for the title). -->
                <Button size="icon-sm" variant="ghost" class="hidden sm:inline-flex" :aria-label="`Move ${t.title} up`" :disabled="i === 0" @click="step(t, -1)"><ChevronUp /></Button>
                <Button size="icon-sm" variant="ghost" class="hidden sm:inline-flex" :aria-label="`Move ${t.title} down`" :disabled="i === open.length - 1" @click="step(t, 1)"><ChevronDown /></Button>
              </template>
            </div>
          </div>
          <div v-if="expanded === t.id" :class="cn('mt-2.5 flex flex-col gap-3 border-t pt-3', mayWrite ? 'sm:ml-[4.875rem]' : 'sm:ml-[3.75rem]')">
            <Textarea
              :model-value="editing[t.id]?.body ?? t.body"
              rows="3"
              placeholder="Details, links, what done means"
              aria-label="Details"
              :disabled="!mayWrite"
              @update:model-value="(v: string | number) => (editing[t.id] = { body: String(v) })"
              @blur="editing[t.id] && editing[t.id].body !== t.body && patch(t, { body: editing[t.id].body })"
            />
            <fieldset :disabled="!mayWrite" class="flex flex-wrap items-center gap-2">
              <SimpleSelect :model-value="t.priority" :options="priorityOptions" class="w-28" aria-label="Priority" @update:model-value="(v) => v && patch(t, { priority: v as Todo['priority'] })" />
              <SimpleSelect :model-value="t.assigneeAgentId ? agents.data.value?.items.find((a) => a.id === t.assigneeAgentId)?.name : undefined" :options="agentOptions" allow-empty empty-label="No agent" class="w-52" aria-label="Agent" @update:model-value="(v) => patch(t, { agent: v ?? null })" />
              <Input :model-value="t.assigneeName ?? ''" placeholder="Person (username or email)" class="w-full sm:w-56" aria-label="Person" @change="(e: Event) => patch(t, { assignee: (e.target as HTMLInputElement).value || null })" />
              <SimpleSelect :model-value="t.status" :options="['open', 'in_progress', 'blocked', 'done', 'cancelled']" class="w-36" aria-label="Status" @update:model-value="(v) => v && patch(t, { status: v as Todo['status'] })" />
              <span class="flex gap-1 sm:hidden">
                <Button size="sm" variant="outline" :aria-label="`Move ${t.title} up`" :disabled="i === 0" @click="step(t, -1)"><ChevronUp /> Up</Button>
                <Button size="sm" variant="outline" :aria-label="`Move ${t.title} down`" :disabled="i === open.length - 1" @click="step(t, 1)"><ChevronDown /> Down</Button>
              </span>
              <Button size="sm" variant="ghost" class="ml-auto text-destructive" @click="remove(t)"><Trash2 /> Delete</Button>
            </fieldset>

            <div v-if="t.priorityReason" class="flex flex-col gap-1 rounded-lg border border-ai/30 bg-ai/5 p-3 text-sm">
              <p class="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Sparkles class="size-3.5 text-ai" /> Why it is here<template v-if="t.prioritizedAt"> · prioritized <RelativeTime :value="t.prioritizedAt" /></template></p>
              <p>{{ t.priorityReason }}</p>
              <p class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span v-if="t.estimate">Estimate {{ t.estimate }} ({{ ESTIMATES[t.estimate] }})</span>
                <template v-if="t.suggestedAgentName">
                  <span>· suggested agent {{ agentTitle(t.suggestedAgentName) }}</span>
                  <Button v-if="mayWrite && !t.assigneeAgentId && agentOptions.some((o) => o.value === t.suggestedAgentName)" size="xs" variant="outline" @click="patch(t, { agent: t.suggestedAgentName })"><Bot /> Assign</Button>
                </template>
              </p>
            </div>

            <label v-if="shared && mayWrite" class="flex items-start gap-2 text-sm">
              <Switch :model-value="isNight(t)" class="mt-0.5" @update:model-value="toggleNight(t)" />
              <span>
                For the night shift
                <span class="block text-xs text-muted-foreground">An agent works on it in the night window: the assigned one, else the suggested one, else the night shift's fallback agent.</span>
              </span>
            </label>

            <div class="flex flex-col gap-1.5">
              <span class="text-xs font-medium text-muted-foreground">Tags</span>
              <TagInput :model-value="plainTags(t)" :disabled="!mayWrite" placeholder="add a tag" aria-label="Tags" @update:model-value="(v: string[]) => patch(t, { tags: [...t.tags.filter((x) => !plainTags(t).includes(x)), ...v] })" />
            </div>

            <ul v-if="sortedLinks(t).length" class="flex flex-col gap-1 text-sm" aria-label="Links">
              <li v-for="l in sortedLinks(t)" :key="l.url" class="flex min-w-0 items-center gap-1.5">
                <component :is="LINK_ICON[l.kind] ?? Link2" class="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <a :href="l.url" target="_blank" rel="noopener" class="min-w-0 truncate underline-offset-2 hover:underline">{{ linkLabel(l) }}</a>
                <span v-if="l.title && l.title !== linkLabel(l)" class="hidden truncate text-xs text-muted-foreground sm:inline">{{ l.title }}</span>
                <ExternalLink class="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
              </li>
            </ul>

            <TodoRunSummary v-if="t.runId" :run-id="t.runId" />

            <div v-if="t.resultMd" class="rounded-lg bg-surface-sunken p-3">
              <p class="mb-1 text-xs font-medium text-muted-foreground">Result</p>
              <MarkdownView :source="t.resultMd" />
            </div>
            <p class="text-xs text-muted-foreground">
              Added <RelativeTime :value="t.createdAt" /><template v-if="t.createdByName"> by {{ t.createdByName }}</template>
              <template v-if="t.fingerprint"> · intake <span class="font-mono">{{ t.fingerprint }}</span></template>
            </p>
          </div>
        </li>
      </ol>

      <template v-if="showDone && closed.length">
        <h2 class="section-title mt-2 flex items-center gap-2">Done <span class="text-sm font-normal text-muted-foreground tabular-nums">{{ closed.length }}</span></h2>
        <ul class="flex flex-col divide-y rounded-xl border bg-card px-1 shadow-xs">
          <li v-for="t in closed" :key="t.id" class="flex flex-wrap items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
            <span class="grid size-5 shrink-0 place-items-center rounded-full bg-success-soft text-success" aria-hidden="true"><Check class="size-3" /></span>
            <span class="min-w-0 flex-1 truncate line-through">{{ t.title }}</span>
            <a v-for="l in pullRequests(t)" :key="l.url" :href="l.url" target="_blank" rel="noopener" class="rounded-full" :title="l.title ?? l.url">
              <Badge variant="success" class="font-normal"><GitPullRequest /> {{ linkLabel(l) }}</Badge>
            </a>
            <RouterLink v-if="t.runId" :to="`/agents/runs/${t.runId}`" class="text-xs underline-offset-2 hover:underline">run</RouterLink>
            <Button v-if="mayWrite" size="icon-sm" variant="ghost" :aria-label="`Reopen ${t.title}`" @click="patch(t, { status: 'open' })"><RotateCcw /></Button>
          </li>
        </ul>
      </template>
    </template>
  </div>
</template>
